import { useCallback, useEffect, useRef, useState, type FC } from "react";
import { useAuiState } from "@assistant-ui/react";
import { Loader2Icon, StopCircleIcon, Volume2Icon } from "lucide-react";
import { TooltipIconButton } from "@/components/tooltip-icon-button";
import { speakText, type TtsHandle } from "@/lib/tts-player";
import { messageToPlainText } from "@/lib/message-text";
import { useVoiceStore } from "@/stores/voice-store";
import { toBcp47 } from "@/i18n";
import { useLocale, useT } from "@/locale-context";

type PlaybackState = "idle" | "loading" | "playing";

export const SpeakButton: FC = () => {
  const t = useT();
  const locale = useLocale();
  const messageText = useAuiState((s) =>
    messageToPlainText(s.message as unknown),
  );
  const setSpeaking = useVoiceStore((s) => s.setSpeaking);

  const [state, setState] = useState<PlaybackState>("idle");
  const handleRef = useRef<TtsHandle | null>(null);

  const stop = useCallback(() => {
    handleRef.current?.stop();
    handleRef.current = null;
    setSpeaking(false);
    setState("idle");
  }, [setSpeaking]);

  useEffect(() => () => stop(), [stop]);

  const handleClick = useCallback(async () => {
    if (state === "loading") return;

    if (state === "playing") {
      stop();
      return;
    }

    if (!messageText) {
      console.warn("[SpeakButton] no text to speak");
      return;
    }

    setState("loading");
    const handle = speakText(messageText, toBcp47(locale));
    handleRef.current = handle;
    setSpeaking(true);
    setState("playing");
    await handle.ended;
    if (handleRef.current === handle) {
      handleRef.current = null;
      setSpeaking(false);
      setState("idle");
    }
  }, [locale, messageText, setSpeaking, state, stop]);

  const tooltip =
    state === "playing"
      ? t("thread.voice.stopPlayback")
      : t("thread.voice.play");

  return (
    <TooltipIconButton
      tooltip={tooltip}
      aria-label={tooltip}
      aria-pressed={state === "playing"}
      disabled={state === "loading" || !messageText}
      onClick={handleClick}
    >
      {state === "loading" ? (
        <Loader2Icon className="size-4 animate-spin" />
      ) : state === "playing" ? (
        <StopCircleIcon className="size-4" />
      ) : (
        <Volume2Icon className="size-4" />
      )}
    </TooltipIconButton>
  );
};
